import { useState, useEffect } from "react";
import { Ticket, Plus, Trash2, Copy, CheckCircle2, XCircle, Clock, Users, Percent, Eye, EyeOff, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface PromoCode {
  id: string;
  code: string;
  discount_percent: number;
  max_uses: number | null;
  current_uses: number;
  starts_at: string;
  expires_at: string;
  is_active: boolean;
  description: string | null;
  created_at: string;
}

interface Redemption {
  id: string;
  promo_code_id: string;
  user_id: string;
  redeemed_at: string;
  discount_percent: number;
  original_plan: string | null;
}

const AdminPromoManager = () => {
  const { user } = useAuth();
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [profiles, setProfiles] = useState<Map<string, { full_name: string | null; email: string | null }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedPromo, setExpandedPromo] = useState<string | null>(null);

  // Create form state
  const [newCode, setNewCode] = useState("");
  const [newDiscount, setNewDiscount] = useState(10);
  const [newMaxUses, setNewMaxUses] = useState<string>("");
  const [newDuration, setNewDuration] = useState(7);
  const [newDurationUnit, setNewDurationUnit] = useState<"days" | "hours">("days");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    const [{ data: promoData }, { data: redemptionData }] = await Promise.all([
      supabase.from("promo_codes").select("*").order("created_at", { ascending: false }),
      supabase.from("promo_redemptions").select("*").order("redeemed_at", { ascending: false }),
    ]);
    setPromos((promoData as any[]) || []);
    setRedemptions((redemptionData as any[]) || []);

    // Fetch profiles for redemption users
    if (redemptionData && redemptionData.length > 0) {
      const userIds = [...new Set(redemptionData.map((r: any) => r.user_id))];
      const { data: profileData } = await supabase.from("profiles").select("id, full_name, email").in("id", userIds);
      const map = new Map<string, { full_name: string | null; email: string | null }>();
      (profileData || []).forEach((p: any) => map.set(p.id, { full_name: p.full_name, email: p.email }));
      setProfiles(map);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    const channel = supabase
      .channel("admin-promos")
      .on("postgres_changes", { event: "*", schema: "public", table: "promo_codes" }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "promo_redemptions" }, () => fetchData())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const generateCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "SF-";
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
    setNewCode(code);
  };

  const handleCreate = async () => {
    if (!newCode.trim()) { toast.error("Enter a promo code"); return; }
    if (newDiscount < 1 || newDiscount > 100) { toast.error("Discount must be 1-100%"); return; }

    setCreating(true);
    const expiresAt = new Date();
    if (newDurationUnit === "days") expiresAt.setDate(expiresAt.getDate() + newDuration);
    else expiresAt.setHours(expiresAt.getHours() + newDuration);

    const { error } = await supabase.from("promo_codes").insert({
      code: newCode.toUpperCase().trim(),
      discount_percent: newDiscount,
      max_uses: newMaxUses ? parseInt(newMaxUses) : null,
      expires_at: expiresAt.toISOString(),
      description: newDescription.trim() || null,
      created_by: user?.id,
    } as any);

    if (error) {
      if (error.message.includes("duplicate")) toast.error("This code already exists");
      else toast.error(error.message);
    } else {
      toast.success(`Promo code ${newCode.toUpperCase()} created!`);
      setShowCreate(false);
      setNewCode("");
      setNewDiscount(10);
      setNewMaxUses("");
      setNewDuration(7);
      setNewDescription("");
      fetchData();
    }
    setCreating(false);
  };

  const toggleActive = async (promo: PromoCode) => {
    await supabase.from("promo_codes").update({ is_active: !promo.is_active } as any).eq("id", promo.id);
    toast.success(promo.is_active ? "Promo deactivated" : "Promo activated");
    fetchData();
  };

  const deletePromo = async (id: string) => {
    await supabase.from("promo_codes").delete().eq("id", id);
    toast.success("Promo code deleted");
    fetchData();
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const getStatus = (promo: PromoCode) => {
    if (!promo.is_active) return { label: "Inactive", color: "app-text-muted" };
    if (new Date(promo.expires_at) < new Date()) return { label: "Expired", color: "app-red" };
    if (promo.max_uses && promo.current_uses >= promo.max_uses) return { label: "Used Up", color: "app-red" };
    return { label: "Active", color: "app-green" };
  };

  const timeLeft = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h left`;
    return `${hours}h left`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[hsl(var(--app-gold))] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center">
            <Ticket className="w-5 h-5 text-[hsl(var(--app-gold))]" />
          </div>
          <div>
            <h2 className="font-bebas text-xl text-foreground">PROMO CODES</h2>
            <p className="text-[10px] text-muted-foreground">{promos.length} codes · {redemptions.length} redemptions</p>
          </div>
        </div>
        <button
          onClick={() => { setShowCreate(!showCreate); if (!showCreate) generateCode(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-xs font-bold hover:brightness-110 transition-all"
        >
          <Plus className="w-4 h-4" /> New Code
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bebas text-lg text-foreground">CREATE PROMO CODE</h3>
            <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Code</label>
              <div className="flex gap-2">
                <input
                  value={newCode}
                  onChange={e => setNewCode(e.target.value.toUpperCase())}
                  placeholder="SF-XXXX"
                  className="flex-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-foreground font-mono outline-none focus:border-[hsl(var(--app-gold))]"
                />
                <button onClick={generateCode} className="px-3 py-2 rounded-lg bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] text-xs text-muted-foreground hover:text-foreground transition">
                  Generate
                </button>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Discount %</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={5}
                  max={100}
                  step={5}
                  value={newDiscount}
                  onChange={e => setNewDiscount(parseInt(e.target.value))}
                  className="flex-1 accent-[hsl(var(--app-gold))]"
                />
                <span className="text-lg font-bold text-[hsl(var(--app-gold))] w-12 text-right">{newDiscount}%</span>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Duration</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  value={newDuration}
                  onChange={e => setNewDuration(parseInt(e.target.value) || 1)}
                  className="w-20 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-foreground outline-none focus:border-[hsl(var(--app-gold))]"
                />
                <select
                  value={newDurationUnit}
                  onChange={e => setNewDurationUnit(e.target.value as "days" | "hours")}
                  className="px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-foreground outline-none"
                >
                  <option value="days">Days</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Max Uses (empty = unlimited)</label>
              <input
                type="number"
                min={1}
                value={newMaxUses}
                onChange={e => setNewMaxUses(e.target.value)}
                placeholder="Unlimited"
                className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-foreground outline-none focus:border-[hsl(var(--app-gold))]"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Description (internal note)</label>
            <input
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              placeholder="e.g. VIP client discount, launch promo..."
              className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-foreground outline-none focus:border-[hsl(var(--app-gold))]"
            />
          </div>

          {/* Preview */}
          <div className="bg-[hsl(var(--app-surface-2))] rounded-xl p-4 border border-[hsl(var(--app-border-subtle))]">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Preview</p>
            <div className="flex items-center gap-4">
              <div className="font-mono text-xl font-bold text-[hsl(var(--app-gold))] tracking-wider">{newCode || "SF-XXXX"}</div>
              <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] text-xs font-bold">{newDiscount}% OFF</span>
              <span className="text-xs text-muted-foreground">{newDuration} {newDurationUnit}</span>
              {newMaxUses && <span className="text-xs text-muted-foreground">· {newMaxUses} uses</span>}
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={creating || !newCode.trim()}
            className="w-full py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold text-sm hover:brightness-110 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {creating ? <div className="w-4 h-4 border-2 border-[hsl(var(--app-bg))] border-t-transparent rounded-full animate-spin" /> : <><Ticket className="w-4 h-4" /> Create Promo Code</>}
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-4 text-center">
          <p className="font-mono text-2xl font-bold text-[hsl(var(--app-gold))]">{promos.filter(p => getStatus(p).label === "Active").length}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Active</p>
        </div>
        <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-4 text-center">
          <p className="font-mono text-2xl font-bold text-[hsl(var(--app-green))]">{redemptions.length}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Redeemed</p>
        </div>
        <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-4 text-center">
          <p className="font-mono text-2xl font-bold text-[hsl(var(--app-blue))]">{promos.filter(p => getStatus(p).label === "Expired").length}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Expired</p>
        </div>
      </div>

      {/* Promo List */}
      {promos.length === 0 ? (
        <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-2xl p-8 text-center">
          <Ticket className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No promo codes yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map(promo => {
            const status = getStatus(promo);
            const isExpanded = expandedPromo === promo.id;
            const promoRedemptions = redemptions.filter(r => r.promo_code_id === promo.id);

            return (
              <div key={promo.id} className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-2xl overflow-hidden">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      status.label === "Active" ? "bg-[hsl(var(--app-green)/0.15)] border border-[hsl(var(--app-green)/0.3)]" :
                      status.label === "Expired" ? "bg-[hsl(var(--app-red)/0.1)] border border-[hsl(var(--app-red)/0.2)]" :
                      "bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))]"
                    }`}>
                      <Ticket className={`w-5 h-5 text-[hsl(var(--${status.color}))]`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-foreground tracking-wider">{promo.code}</span>
                        <button onClick={() => copyCode(promo.code)} className="text-muted-foreground hover:text-[hsl(var(--app-gold))]">
                          <Copy className="w-3 h-3" />
                        </button>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold bg-[hsl(var(--${status.color})/0.15)] text-[hsl(var(--${status.color}))]`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs font-bold text-[hsl(var(--app-gold))]">{promo.discount_percent}% OFF</span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {timeLeft(promo.expires_at)}
                        </span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Users className="w-3 h-3" /> {promo.current_uses}/{promo.max_uses || "∞"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setExpandedPromo(isExpanded ? null : promo.id)}
                      className="p-2 rounded-lg hover:bg-[hsl(var(--app-surface-2))] text-muted-foreground transition"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleActive(promo)}
                      className={`p-2 rounded-lg hover:bg-[hsl(var(--app-surface-2))] transition ${promo.is_active ? "text-[hsl(var(--app-green))]" : "text-muted-foreground"}`}
                      title={promo.is_active ? "Deactivate" : "Activate"}
                    >
                      {promo.is_active ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => deletePromo(promo.id)}
                      className="p-2 rounded-lg hover:bg-[hsl(var(--app-red)/0.1)] text-muted-foreground hover:text-[hsl(var(--app-red))] transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-[hsl(var(--app-border-subtle))] px-4 pb-4">
                    {promo.description && (
                      <p className="text-xs text-muted-foreground mt-3 mb-2 italic">"{promo.description}"</p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[10px]">
                      <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-2">
                        <span className="text-muted-foreground block">Created</span>
                        <span className="text-foreground font-medium">{new Date(promo.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-2">
                        <span className="text-muted-foreground block">Starts</span>
                        <span className="text-foreground font-medium">{new Date(promo.starts_at).toLocaleDateString()}</span>
                      </div>
                      <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-2">
                        <span className="text-muted-foreground block">Expires</span>
                        <span className="text-foreground font-medium">{new Date(promo.expires_at).toLocaleString()}</span>
                      </div>
                      <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-2">
                        <span className="text-muted-foreground block">Redemptions</span>
                        <span className="text-foreground font-medium">{promoRedemptions.length}</span>
                      </div>
                    </div>

                    {/* Redemption list */}
                    {promoRedemptions.length > 0 && (
                      <div className="mt-3">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Redeemed By</p>
                        <div className="space-y-1.5">
                          {promoRedemptions.map(r => {
                            const profile = profiles.get(r.user_id);
                            return (
                              <div key={r.id} className="flex items-center justify-between bg-[hsl(var(--app-surface-2))] rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="w-3 h-3 text-[hsl(var(--app-green))]" />
                                  <span className="text-xs text-foreground">{profile?.full_name || profile?.email || "User"}</span>
                                </div>
                                <span className="text-[10px] text-muted-foreground">{new Date(r.redeemed_at).toLocaleString()}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPromoManager;
